import React from "react";
import { CalendarX2, Calculator, AlertTriangle, PiggyBank, LayoutDashboard, BellRing, FileText, Lock, HandCoins, Percent, Check } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import PageHero from "@/components/ui-kit/PageHero";
import { Container, Section, Eyebrow } from "@/components/ui-kit/Section";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import DualAudience from "@/components/blocks/DualAudience";
import FinalCta from "@/components/blocks/FinalCta";
import Cta from "@/components/ui-kit/Cta";
import { IMAGERY } from "@/lib/site";

const PAINS = [
  { icon: CalendarX2, text: "Four deadlines a year that always seem to arrive at once" },
  { icon: Calculator, text: "Guessing a set aside number and hoping it is enough" },
  { icon: AlertTriangle, text: "Quiet anxiety about penalties for underpaying" },
];

const BENEFITS = [
  {
    icon: PiggyBank,
    title: "Automatic set aside on every deposit",
    body: "The moment income is detected, your chosen percentage is routed toward federal and state taxes. No manual math, no transfers to remember.",
  },
  {
    icon: LayoutDashboard,
    title: "A dashboard that shows the full picture",
    body: "See your deposits, the amount routed, the fee on each transaction, and your running totals. Everything is recorded and auditable.",
  },
  {
    icon: BellRing,
    title: "Notifications when something needs you",
    body: "If a bank link breaks or a deposit needs review, ETAP tells you and pauses routing on that account until it is sorted.",
  },
  {
    icon: FileText,
    title: "Reports ready when you file",
    body: "Quarterly and annual reports are prepared as you go, so filing time is a download, not a scramble through statements.",
  },
];

const OBJECTIONS = [
  {
    icon: Lock,
    q: "Is my bank safe?",
    a: "Yes. The connection is read only through Plaid. ETAP reads transactions to detect income and never sees or stores your bank login credentials.",
  },
  {
    icon: HandCoins,
    q: "Who holds my money?",
    a: "Nobody in between. ETAP never holds your funds. Money moves from your linked account to tax authorities through authorized rails. ETAP is not a bank.",
  },
  {
    icon: Percent,
    q: "What does it cost?",
    a: "A flat 0.5 percent of each routed amount, enforced as a hard ceiling in code. No monthly fee, no setup fee, and nothing charged on money you do not route.",
  },
];

export default function Contractors() {
  return (
    <>
      <Seo
        title="ETAP for Contractors | Never scramble at tax deadlines"
        description="Freelancers and 1099 contractors: ETAP routes your set aside on every deposit and keeps reports ready to file. Read only bank access, 0.5 percent flat."
        path="/contractors"
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "For Contractors", path: "/contractors" },
          ]),
        ]}
      />

      <PageHero
        eyebrow="For contractors"
        title="Stop guessing what you owe every quarter"
        subtitle="If you are a freelancer, gig worker, or 1099 contractor, the tax you owe does not come out automatically. ETAP fixes that. Set your percentage once and every deposit is handled."
      >
        <div className="mt-8">
          <Cta to="/get-started" variant="primary" size="lg" location="contractors-hero" withArrow testId="contractors-hero-cta">
            Start Free Setup
          </Cta>
        </div>
      </PageHero>

      {/* Pain + image */}
      <Section className="bg-canvas pt-4">
        <Container>
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <Eyebrow>The quarterly scramble</Eyebrow>
                <h2 className="mt-4 font-display text-[clamp(1.75rem,3.6vw,2.75rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                  Working for yourself is great, until estimated taxes come due
                </h2>
                <p className="mt-4 text-lg text-etap-deep-600">
                  You do the work, you send the invoice, and then a few times a year you have to become your own payroll
                  department. It is easy to under set aside, miss a date, or move money around at the last minute.
                </p>
              </Reveal>
              <RevealGroup className="mt-7 space-y-3">
                {PAINS.map((p) => (
                  <RevealItem
                    key={p.text}
                    className="flex items-start gap-3 rounded-2xl border border-etap-deep-100 bg-white p-4 text-etap-deep-700 shadow-etap-sm"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-etap-primary-50 text-etap-primary">
                      <p.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="pt-1.5 text-sm font-medium">{p.text}</span>
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={0.1} className="relative">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-4 -z-10 rounded-[2rem] bg-etap-mint-200/50 blur-2xl"
                />
                <img
                  src={IMAGERY.contractor}
                  alt="An independent contractor working on a laptop"
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-[1.75rem] border border-white object-cover shadow-etap-xl"
                />
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* Benefits */}
      <Section className="bg-white">
        <Container>
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>What you get</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                Set the rule once, then let each deposit take care of itself
              </h2>
            </Reveal>
          </div>
          <RevealGroup className="mt-12 grid gap-6 md:grid-cols-2">
            {BENEFITS.map((b) => (
              <RevealItem
                key={b.title}
                className="rounded-3xl border border-etap-deep-100 bg-canvas p-8 shadow-etap-sm transition-shadow duration-300 hover:shadow-etap-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-etap-deep text-white">
                  <b.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-etap-deep-800">{b.title}</h3>
                <p className="mt-3 text-etap-deep-600">{b.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </Container>
      </Section>

      {/* Objections */}
      <Section className="bg-canvas">
        <Container>
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>Fair questions</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                The three things people want to know first
              </h2>
            </Reveal>
          </div>
          <RevealGroup className="mt-12 grid gap-6 md:grid-cols-3">
            {OBJECTIONS.map((o) => (
              <RevealItem key={o.q} className="rounded-3xl border border-etap-deep-100 bg-white p-7 shadow-etap-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-etap-mint-200 text-etap-deep-700">
                  <o.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-etap-deep-800">{o.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-etap-deep-600">{o.a}</p>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal className="mt-10">
            <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-etap-deep-600">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-etap-primary" aria-hidden="true" /> No monthly fee
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-etap-primary" aria-hidden="true" /> No setup fee
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-etap-primary" aria-hidden="true" /> Cancel or pause anytime
              </li>
            </ul>
          </Reveal>
        </Container>
      </Section>

      <Section className="bg-white pt-0">
        <Container>
          <DualAudience location="contractors-dual" />
        </Container>
      </Section>

      <FinalCta
        location="contractors-final"
        title="Your next deposit could handle its own taxes"
        subtitle="Set your percentage, link your account read only, and let ETAP route your set aside from here on."
      />
    </>
  );
}
